import { sanitize } from '@brandfolder/utilities';
import { t, Trans } from '@lingui/macro';
import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  FunctionComponent } from 'react';
import ReactTags, { TagComponentProps } from 'react-tag-autocomplete';

import getSearchableThings from '@api/searchable-things';
import ClipboardCopy from '@components/common/clipboard_copy/main';
import { Locale } from '@components/common/language_menu/languagesMap';
import { TextButton, TextWarningButton } from '@components/library/button/index';
import Label from '@components/library/labels/PrimaryLabel';

import assetModalContext from '../asset_modal_context';
import { ActionTypes, ReactTag, AssetDetailsDispatch } from '../EditTabTypes';

import './styles/_tags.scss';
import SuggestedTags from './suggestedTags';

interface TagsProps {
  tags: ReactTag[];
  tagSearchSuggestions: ReactTag[] | null;
}

interface TagsInputScopedContext {
  state: {
    editState: TagsProps;
    initialData: {
      assetKey: string;
    };
    ugtLocale: Locale | '';
  };
  dispatch: AssetDetailsDispatch;
}

interface SearchableResponse {
  tags: Array<string>;
  filetypes: Array<string>;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  custom_fields: Array<{
    [key: string]: string
  }>
}

const tagDelimiters = [9, 13, 188];

const TagComponent = (props: TagComponentProps): JSX.Element => useMemo(() => (
  // eslint-disable-next-line max-len
  <div className={`${props.classNames.selectedTag} ${(props.tag as ReactTag).auto_generated ? 'auto-generated' : ''} ${(props.tag as ReactTag).source === 'people_tagging' ? 'ppl-tag' : ''}`}>
    <span className={props.classNames.selectedTagName}>{props.tag.name}</span>
    <button
      aria-label={t`Remove ${props.tag.name} tag`}
      className="react-tags__remove"
      onClick={props.onDelete}
      type="button"
    >
      <span className="bff-close" />
    </button>
  </div>
), [props.tag]);
// this dependency array intentionally only includes props.tag so that the tag components
// are not rerendered every time the search suggestions are filtered

const TagsInput: FunctionComponent = () => {
  const { state, dispatch }: TagsInputScopedContext = useContext(assetModalContext);
  const { tags }: TagsProps = state.editState;

  const [searchSuggestions, setSearchSuggestions] = useState<ReactTag[]>(null);
  const [inputFocus, setInputFocus] = useState(false);

  useEffect(() => {
    setSearchSuggestions(null);
    setInputFocus(false);
  }, [state.ugtLocale]);

  useEffect(() => {
    const getSearchSuggestions = async (): Promise<void> => {
      const response: SearchableResponse = await getSearchableThings({
        resourceType: BFG.resource.type,
        resourceKey: BFG.resource.key,
        ugtLocale: state.ugtLocale || undefined
      });
      if (response && response.tags) {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        const typedTags: ReactTag[] = response.tags.map((name) => ({ name, auto_generated: false, id: name }));
        setSearchSuggestions(typedTags);
      } else {
        setSearchSuggestions([]);
      }
    };

    if (inputFocus && searchSuggestions === null) {
      getSearchSuggestions();
    }
  }, [inputFocus, state.ugtLocale, searchSuggestions]);

  const suggestionsFilter = (suggestion: ReactTag | null, query: string): boolean => {
    const queryContained = !!suggestion?.name?.toLowerCase().includes(query.toLowerCase());
    return queryContained && !tags.some(({ name }) => name === suggestion?.name);
  };

  // TODO add duplicate detection on keyup and display `Tag already exists` message
  // const isDuplicateTag = (tag: ReactTag): boolean => (
  //   tags.map((t) => (t.name.toUpperCase())).includes(tag.name.toUpperCase())
  // );

  const addNewTags = (tag: ReactTag): void => {
    const newTags = [];
    tag.name.split(',').forEach((tagName) => {
      if (tagName) {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        newTags.push({ name: sanitize(tagName), id: sanitize(tagName), auto_generated: false });
      }
    });
    const newModifiedTags = [
      ...tags,
      ...newTags,
    ];
    dispatch({ type: ActionTypes.UpdateAsset, payload: { tags: newModifiedTags } });
  };

  const deleteTags = (index: number): void => {
    const deletedTag = tags[index];
    const newModifiedTags = [
      ...tags
    ];
    newModifiedTags.splice(index, 1);

    // Track tag removal with Amplitude
    if (window.BFG?.amplitude?.enabled && window.amplitudeService && deletedTag) {
      window.amplitudeService.trackEvent('RemoveTag-ButtonClicked', {
        elementName: 'RemoveTag',
        productArea: 'AssetModal',
        featureArea: 'EditTab',
        'key properties': {
          assetId: state.initialData.assetKey,
          tagName: deletedTag.name
        }
      });
    }

    dispatch({ type: ActionTypes.UpdateAsset, payload: { tags: newModifiedTags } });
  };

  const tagsString: string | undefined = tags?.map((tag) => (tag.name)).join(',');

  return useMemo(() => (
    <>
      <div className="tags-input-container">
        <div className="copy-link-container">
          <Label attributes={{ htmlFor: 'tags-input' }}>
            <Trans>Tags</Trans>
          </Label>
          <div className="tags-actions-container">
            {Boolean(tagsString) && (
              <>
                <ClipboardCopy
                  check
                  showFeedbackAsTooltip={false}
                  textToCopy={tagsString}
                >
                  <TextButton
                    icon="bff-copy"
                    iconSize={13}
                  >
                    <Trans>Copy Tags</Trans>
                  </TextButton>
                </ClipboardCopy>
                <TextWarningButton
                  icon="bff-trash"
                  iconSize={13}
                  onClick={(): void => dispatch({ type: ActionTypes.UpdateAsset, payload: { tags: [] } })}
                >
                  <Trans>Delete Tags</Trans>
                </TextWarningButton>
              </>
            )}
          </div>
        </div>
        <div className="tags-input">
          <div className="tags-container">
            <ReactTags
              allowBackspace
              allowNew
              autofocus={false}
              delimiters={tagDelimiters}
              handleAddition={(tag: ReactTag): void => addNewTags(tag)}
              handleDelete={(i: number): void => deleteTags(i)}
              handleFocus={(): void => setInputFocus(true)}
              minQueryLength={1}
              placeholder=""
              suggestions={searchSuggestions || []}
              suggestionsFilter={suggestionsFilter}
              tagComponent={TagComponent}
              tags={tags}
            />
          </div>
        </div>
      </div>
      {state.initialData.assetKey && (
        <div className="tags-input-container">
          <Label attributes={{ htmlFor: 'suggested-tags' }}>
            <Trans>Suggested Tags</Trans>
          </Label>
          <div className="suggested-tags">
            <SuggestedTags addReactTag={addNewTags} />
          </div>
        </div>
      )}
    </>
  ), [dispatch, tags, searchSuggestions]);
};

export default TagsInput;
